import { Button } from "@/src/components/Button";
import { TextField } from "@/src/components/Fields/TextField";
import { WebShopIcon } from "@/src/components/WebShopIcon";
import { zodResolver } from "@hookform/resolvers/zod";
import { Box, Typography } from "@mui/material";
import { useEffect, useState } from "react";
import { useForm, useFormContext } from "react-hook-form";
import zod, { z } from "zod";
import { useContextStore } from "@/src/stores/useContextStore";
import { useHandleApiCall } from "../pages/StepPage/components/apiCalls/useHandleApiCall";
import {ApiCallsTypes, FlowCodes} from "../pages/StepPage/components/apiCalls/contants";
import { runAction } from "@/src/api/services/action";
import { formatFormDataToVarList } from "@/src/shared/utils/form";
import {
  COUNTRY_TRAVEL_ZONE_CONST,
  DAYS_ABROAD_CONST,
  FOR_TOURISTS_CONST,
  INSURED_COUNT_VAR_LIST,
  INSURED_COUNT_VAR_LIST_VISA_CENTRE,
  INSURED_TOURISTS_CONST,
  PLACE_OF_STAY_ABROAD_CONST,
  TERRORISM_CONST,
  TRAVEL_PURPOSE_CONST,
  WORK_CONST,
} from "@/src/components/ProposalTable/lib/const";
import { isObject, toNumber } from "lodash";
import { useProcessInstanceStore } from "@/src/stores/useProcessInstanceStore";
import { useSearchParams } from "next/navigation";
import { PROCESS_INSTANCE_QUERY } from "@/src/shared/const";
import { getColContextValueString } from "@/src/components/ProposalTable/lib/utils";
import { StepAnalytics } from "@/src/shared/models/analytics";
import { useAnalytics } from "@/src/components/pages/StepPage/components/useAnalytics/useAnalytics";
import { maskFunctions } from "@/src/shared/utils/mask";
import { useUpdateField } from "@/src/hooks/useFieldDebouncedCallback";

const promocodeSchema = z.object({ promocode: z.string().default("") });

type PromocodeSchema = zod.infer<typeof promocodeSchema>;

// Products whose premium is priced per calculationCategory (Tourism's Standart/Prestige,
// Visa Centre's Franchise0/Franchise50) need the discounted premium re-fetched from the
// tariff calculator for the currently picked category after a promocode is validated —
// the generic backend discount (ValidatePromocodeAction) only touches the flat
// premium/finalPremium fields, not the per-category ones. Products not listed here don't
// need this extra round trip; the backend discount is sufficient.
const PROMOCODE_TARIFF_RECALC_CONFIG: Partial<
  Record<string, { tariffCalculatorActionName: ApiCallsTypes; insuredCountVarList: string[] }>
> = {
  [FlowCodes.VZK_NEW]: {
    tariffCalculatorActionName: ApiCallsTypes.TRAVEL_TARIFF_CALCULATOR,
    insuredCountVarList: INSURED_COUNT_VAR_LIST,
  },
  [FlowCodes.VZK_VISA_CENTER]: {
    tariffCalculatorActionName: ApiCallsTypes.VISA_CENTER_TARIFF_CALCULATOR,
    insuredCountVarList: INSURED_COUNT_VAR_LIST_VISA_CENTRE,
  },
};

const useTourismPromocode = () => {
  const searchParams = useSearchParams();
  const processInstanceId = searchParams.get(PROCESS_INSTANCE_QUERY);
  const processInstance = useProcessInstanceStore(
    (state) => state.processInstance,
  );
  const { replaceVariablesObject } = useContextStore();

  const { getValues } = useFormContext();

  const flowCode = getValues("flowCode");
  const tariffRecalcConfig = PROMOCODE_TARIFF_RECALC_CONFIG[flowCode];
  // todo refactor
  const handleTouristsPromocode = async () => {
    const planName = getValues("pickedTravelCalculationCategory");

    const insuredTourists = getValues(INSURED_TOURISTS_CONST) as Record<
      string,
      number
    >;
    if (!isObject(insuredTourists)) {
      return;
    }

    const apiCallActionParams = {
      countryTravelZone: getValues(COUNTRY_TRAVEL_ZONE_CONST),
      work: getValues(WORK_CONST),
      daysAbroad: toNumber(getValues(DAYS_ABROAD_CONST)),
      terrorism: getValues(TERRORISM_CONST),
      travelPurpose: getValues(TRAVEL_PURPOSE_CONST),
      placeOfStayAbroad: getValues(PLACE_OF_STAY_ABROAD_CONST),
    };

    const staticValueKey = getColContextValueString(planName);
    const columnStaticValues = getValues(staticValueKey);
    if (!isObject(columnStaticValues)) {
      return;
    }

    const ageVariables = (
      tariffRecalcConfig?.insuredCountVarList ?? INSURED_COUNT_VAR_LIST
    ).reduce<Record<string, number>>((acc, touristKey) => {
      acc[touristKey] = insuredTourists[touristKey];
      return acc;
    }, {});

    const variables = {
      ...columnStaticValues,
      ...apiCallActionParams,
      ...ageVariables,
      calculationCategory: getValues("pickedTravelCalculationCategory"),
      pickedTravelCalculationCategory: getValues(
        "pickedTravelCalculationCategory",
      ),
      [FOR_TOURISTS_CONST]: getValues("forTourists"),
      insuredCount: getValues("insuredCount"),
    };

    const formattedVariables = formatFormDataToVarList(variables);

    const requestBody: any = {
      id: processInstanceId,
      processId: processInstance.processId,
      activeStepId: processInstance.activeStepId,
      contextInstance: {
        id: processInstance.contextInstance.id,
        variables: formattedVariables,
      },
    };
    const response = await runAction({
      actionName:
        tariffRecalcConfig?.tariffCalculatorActionName ??
        ApiCallsTypes.TRAVEL_TARIFF_CALCULATOR,
      body: requestBody,
    });
    const pickTariffResponse = await runAction({
      actionName: ApiCallsTypes.PICK_TRAVEL_TARIFF_CATEGORY_ACTION,
      body: {
        ...requestBody,
        contextInstance: {
          id: processInstance.contextInstance.id,
          variables: response.contextInstance.variables,
        },
      },
    });
    replaceVariablesObject(pickTariffResponse.contextInstance.variables);
  };
  if (tariffRecalcConfig) {
    return {
      ignorePremiumUpdate: true,
      paramsOverride: [
        { key: "premium" },
        { key: "promocode" },
        { key: "ignorePremiumUpdate" },
      ],
      handleTouristsPromocode,
    };
  }
  return {
    ignorePremiumUpdate: false,
    paramsOverride: undefined,
    handleTouristsPromocode: undefined,
  };
};

const PromocodeListItem = ({
  label,
  value,
}: {
  label: string;
  value: string;
}) => {
  return (
    <Box className="flex items-center gap-1">
      <span className="inline-block whitespace-nowrap">
        <span className={"whitespace-pre-wrap"}>{label}: </span>
        <b className="font-semibold">{value}&nbsp;грн.</b>
      </span>
    </Box>
  );
};

export function Promocode(props: { analytics?: StepAnalytics }) {
  const { analytics } = props;

  const { trackAnalyticEvent } = useAnalytics();

  const { getValues, watch } = useFormContext();
  const updateField = useUpdateField();
  const { control, handleSubmit, setError, reset, getValues: getValuesPromocode } = useForm<PromocodeSchema>({
    resolver: zodResolver(promocodeSchema),
  });
  const { handleApiCallByName } = useHandleApiCall();
  const { handleTouristsPromocode, paramsOverride, ignorePremiumUpdate } =
    useTourismPromocode();

  const [isFieldRevealed, setIsFieldRevealed] = useState(false);
  const [discountInfo, setDiscountInfo] = useState<null | {
    initialPrice: string;
    finalPremium: string;
    discount: string;
  }>();
  const [loading, setLoading] = useState(false);

  const premium = watch("premium");
  const finalPremium = watch("finalPremium");
  const promocode = watch("promocode");

  const revealField = () => {
    trackAnalyticEvent("promo_click")
    setIsFieldRevealed(true);
  };

  useEffect(() => {
    reset({ promocode });
  }, [promocode]);

  useEffect(() => {
    let allPremium = Number(premium);
    const DMSDTPPremium = getValues("DMSDTPPremium_picked_premium") || 0;
    const LegkyiZahystPremium =
      getValues("LegkyiZahystPremium_picked_premium") || 0;
    if (DMSDTPPremium || LegkyiZahystPremium) {
      allPremium =
        Number(premium) + Number(DMSDTPPremium) + Number(LegkyiZahystPremium);
    }
    if (promocode && allPremium !== finalPremium) {
      revealField();
      const initialPrice =
        Number(premium) + Number(DMSDTPPremium) + Number(LegkyiZahystPremium);
      const finalPremium = getValues("finalPremium") || 0;
      setDiscountInfo({
        initialPrice: maskFunctions.thousandSeparator(initialPrice),
        finalPremium: maskFunctions.thousandSeparator(
          getValues("finalPremium") || 0,
        ),
        discount: maskFunctions.thousandSeparator(
          (initialPrice - finalPremium).toFixed(0),
        ),
      });
    }
  }, [premium, finalPremium]);

  const onSubmit = async (data: PromocodeSchema) => {
    try {
      const premium = Number(getValues("premium")) || 0;
      const promocode = data.promocode;
      setLoading(true);
      await handleApiCallByName(ApiCallsTypes.VALIDATE_PROMOCODE, true, {
        formDataOverride: {
          ...getValues(),
          premium,
          promocode,
          ignorePremiumUpdate: ignorePremiumUpdate,
          throwError: true,
        },
        paramsOverride: paramsOverride,
      });
      await updateField('throwError', false)

      await handleTouristsPromocode?.();
    } catch (error) {
      setDiscountInfo(null);
      setError("promocode", { message: "Промокод недійсний." });
      console.error("Error submitting promocode:", error);
    } finally {
      setLoading(false);
    }
  };

  return (
    <form onSubmit={handleSubmit(onSubmit)} className="flex w-full gap-4">
      <>
        {isFieldRevealed ? (
          <Box className="flex w-full flex-col gap-3">
            <Box className={"flex gap-4"}>
              <TextField
                name="promocode"
                className={"max-w-[365px]"}
                control={control}
                InputProps={{
                  inputProps: {
                    className: "[&&&&]:p-3",
                  },
                }}
              />
              <Button
                className="h-fit w-fit grow-0"
                type="submit"
                onClick={() => {
                  trackAnalyticEvent("promo_apply", { promocode: getValuesPromocode("promocode") })
                }}
                loading={loading}
              >
                Застосувати
              </Button>
            </Box>
            {discountInfo && (
              <Box
                className={
                  "flex w-full gap-2 rounded bg-[#8CC83C1A] p-3 text-[15px] leading-[22px] text-dark-gray"
                }
              >
                <Box>
                  <WebShopIcon
                    name="circleCheckMedium"
                    fill="#8CC83C"
                    className="h-5 w-5"
                  />
                </Box>
                <Box>
                  <Box>Промокод застосовано</Box>
                  <PromocodeListItem
                    label={"Сума до оплати"}
                    value={discountInfo.initialPrice}
                  />
                  <PromocodeListItem
                    label={"Сума знижки"}
                    value={discountInfo.discount}
                  />
                  <PromocodeListItem
                    label={"Сума до оплати з урахуванням знижки"}
                    value={discountInfo.finalPremium}
                  />
                </Box>
              </Box>
            )}
          </Box>
        ) : (
          <Typography
            className="cursor-pointer text-[15px] font-semibold uppercase leading-4 text-blue"
            onClick={revealField}
          >
            Маєте промокод?
          </Typography>
        )}
      </>
    </form>
  );
}
